/**
 * 课程 SKU 管理 API（subject-course-sku:SC-18~SC-45 前端）。
 *
 * 对齐后端 product-catalog 模块 REST 契约：
 * - GET   /course-skus            -> Paginated<CourseSkuItem>（筛选 + 分页）
 * - GET   /course-skus/:id        -> CourseSkuItem（详情）
 * - POST  /course-skus            -> CourseSkuItem（新建，初始 DRAFT）
 * - PATCH /course-skus/:id        -> CourseSkuItem（编辑，草稿/已下架可编辑，携带 version）
 * - POST  /course-skus/:id/publish   -> CourseSkuItem（上架，DRAFT/OFF_SHELF -> ON_SHELF）
 * - POST  /course-skus/:id/unpublish -> CourseSkuItem（下架，ON_SHELF -> OFF_SHELF）
 * - POST  /course-skus/:id/disable   -> CourseSkuItem（停用，校验在售商品引用）
 *
 * 路径从 api.config.ts 导入，禁止内联；Transport 统一解包 `{success, data}` 信封。
 * listPrice 单位为「分」（int），前端展示时除以 100 转元。
 */
import { ApiPaths } from './api.config';
import { http } from './transport';
import type { Paginated } from './campuses.api';

/** 学段（gradeStage）。 */
export type GradeStage = 'PRESCHOOL' | 'PRIMARY_LOW' | 'PRIMARY_HIGH' | 'JUNIOR' | 'SENIOR';

/** 扣减单位（deductionUnit，一期恒按次）。 */
export type DeductionUnit = 'PER_SESSION';

/** 课程 SKU 上下架状态（shelfStatus）。 */
export type ShelfStatus = 'DRAFT' | 'ON_SHELF' | 'OFF_SHELF' | 'DISABLED';

/**
 * 课程 SKU 视图（列表项与详情共用）。
 *
 * O-14 起收费模式（chargeMode）自 SKU 契约退役：SKU 回归纯教学标准，
 * 售卖形态（课时包/期限卡）下沉至商品 SKU 配置行（见 products.api SkuChargeMode）。
 */
export interface CourseSkuItem {
  id: string;
  organizationId: string;
  /** 校区归属（按校区办学）：null/缺省 = 机构通用（全校区可见） */
  campusId?: string | null;
  /** 关联科目 ID */
  subjectId: string;
  /** SKU 编码（机构内唯一，创建后不可改） */
  code: string;
  /** SKU 名称 */
  name: string;
  gradeStage: GradeStage;
  /** 教材版本 */
  textbook: string;
  /** 课时标价（最小货币单位-分）：课时包形态行原价与期限行卡面标价估算依据 */
  listPrice: number;
  /** 课时数默认值（购买开通课时数）：商品配置行课时数预填默认 */
  entitlementQuantity: number;
  /** 试课赠送次数（1~3） */
  trialSessionCount: number;
  deductionUnit: DeductionUnit;
  /** 有效天数 */
  validityDays: number;
  /** 每班最多人数（分班自动分组依据） */
  maxClassCapacity: number;
  /** 最多班级数（分班自动分组依据） */
  maxClassCount: number;
  status: ShelfStatus;
  /** 有效班级引用数（一期占位 0，班级模块回填） */
  activeClassRefCount: number;
  /** 乐观锁版本号 */
  version: number;
  /** 更新时间（ISO） */
  updatedAt: string;
}

/** 课程 SKU 列表查询参数（O-14 起收费模式筛选退役）。 */
export interface CourseSkuListQuery {
  name?: string;
  code?: string;
  subjectId?: string;
  gradeStage?: GradeStage;
  status?: ShelfStatus;
  /** 校区归属筛选（按校区办学）：返回该校区 SKU + 机构通用 SKU */
  campusId?: string;
  page?: number;
  pageSize?: number;
}

/** 新建课程 SKU 请求体（无 deductionUnit，后端恒置 PER_SESSION；listPrice 单位分；不携带 chargeMode）。 */
export interface CreateCourseSku {
  subjectId: string;
  /** 校区归属（按校区办学）：null/缺省 = 机构通用（全校区可见）；须为本机构 ACTIVE 校区 */
  campusId?: string | null;
  code: string;
  name: string;
  gradeStage: GradeStage;
  textbook: string;
  listPrice: number;
  entitlementQuantity: number;
  trialSessionCount: number;
  validityDays: number;
  maxClassCapacity: number;
  maxClassCount: number;
}

/** 编辑课程 SKU 请求体（编码与科目创建后不可改，服务端白名单拒绝这两个字段）。 */
export interface UpdateCourseSku extends Omit<CreateCourseSku, 'subjectId' | 'code'> {
  version: number;
}

/** 构造带筛选与分页的 SKU 列表查询串。 */
function buildListQuery(query: CourseSkuListQuery): string {
  const params = new URLSearchParams();
  if (query.name) params.set('name', query.name);
  if (query.code) params.set('code', query.code);
  if (query.subjectId) params.set('subjectId', query.subjectId);
  if (query.gradeStage) params.set('gradeStage', query.gradeStage);
  if (query.status) params.set('status', query.status);
  if (query.campusId) params.set('campusId', query.campusId);
  if (query.page) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  const qs = params.toString();
  return qs ? `${ApiPaths.courseSkus.list}?${qs}` : ApiPaths.courseSkus.list;
}

async function listCourseSkus(query: CourseSkuListQuery = {}): Promise<Paginated<CourseSkuItem>> {
  return http.get<Paginated<CourseSkuItem>>(buildListQuery(query));
}

async function getCourseSku(id: string): Promise<CourseSkuItem> {
  return http.get<CourseSkuItem>(ApiPaths.courseSkus.detail(id));
}

async function createCourseSku(dto: CreateCourseSku): Promise<CourseSkuItem> {
  return http.post<CourseSkuItem>(ApiPaths.courseSkus.create, dto);
}

async function updateCourseSku(id: string, dto: UpdateCourseSku): Promise<CourseSkuItem> {
  return http.patch<CourseSkuItem>(ApiPaths.courseSkus.update(id), dto);
}

async function publishCourseSku(id: string): Promise<CourseSkuItem> {
  return http.post<CourseSkuItem>(ApiPaths.courseSkus.publish(id), {});
}

async function unpublishCourseSku(id: string): Promise<CourseSkuItem> {
  return http.post<CourseSkuItem>(ApiPaths.courseSkus.unpublish(id), {});
}

async function disableCourseSku(id: string): Promise<CourseSkuItem> {
  return http.post<CourseSkuItem>(ApiPaths.courseSkus.disable(id), {});
}

/** 课程 SKU API 唯一对外入口。 */
export const courseSkuApi = {
  listCourseSkus,
  getCourseSku,
  createCourseSku,
  updateCourseSku,
  publishCourseSku,
  unpublishCourseSku,
  disableCourseSku,
};
